import { UsageTrackerContextPage, UsageTrackerService } from '@csp/csp-common-analytics';
import { ApiOptions, Maybe } from '@csp/csp-common-model';
import { getTelemetryService } from '@csp/csp-common-telemetry';
import { retryAsync } from '@csp/csp-common-util';
import { toRestOptions } from '@csp/csp-fe-auth';
import { AnalyticsClient, AnalyticsEventOptions, AnalyticsIdentifyOptions } from '../model/AnalyticsClient';
import { DebugAnalyticsTrackerService } from '../service/DebugAnalyticsTrackerService';
import { AnalyticsEventFactory } from './AnalyticsEventFactory';

const create = (
  apiOptions?: ApiOptions,
  clientOptions?: { locale?: string; anonymousId?: string },
): AnalyticsClient => {
  const track = async (eventName: string, options?: AnalyticsEventOptions): Promise<void> => {
    const trackEvent = AnalyticsEventFactory.createTrackEvent(eventName, {
      ...options,
      locale: options?.locale || clientOptions?.locale,
      anonymousId: options?.anonymousId || clientOptions?.anonymousId,
      properties: options?.properties ?? {},
    });

    if (!DebugAnalyticsTrackerService.isEnabled()) {
      try {
        await retryAsync(UsageTrackerService.track, [trackEvent, toRestOptions(apiOptions)], 2);
      } catch (error) {
        getTelemetryService().trackException({ error: `AnalyticsClientFactory track exception: ${error}` });
      }
    } else {
      DebugAnalyticsTrackerService.track(trackEvent);
    }
  };

  let previousPage: Maybe<UsageTrackerContextPage> = undefined;
  const page = async (pageName: string, options?: AnalyticsEventOptions): Promise<void> => {
    const pageEvent = AnalyticsEventFactory.createPageEvent(pageName, {
      ...options,
      locale: options?.locale ?? clientOptions?.locale,
      anonymousId: options?.anonymousId ?? clientOptions?.anonymousId,
      properties: options?.properties ?? {},
    });

    // properties will always be defined here, but adding the check to make typescript happy.
    if (previousPage && pageEvent.properties) {
      pageEvent.properties.fromPath = previousPage.path;
      pageEvent.properties.fromReferrer = previousPage.referrer ?? '';
      pageEvent.properties.fromSearch = previousPage.search ?? '';
      pageEvent.properties.fromTitle = previousPage.title;
      pageEvent.properties.fromUrl = previousPage.url;
    }

    previousPage = pageEvent?.context?.page;

    if (!DebugAnalyticsTrackerService.isEnabled()) {
      try {
        await retryAsync(UsageTrackerService.page, [pageEvent, toRestOptions(apiOptions)], 2);
      } catch (error) {
        getTelemetryService().trackException({ error: `AnalyticsClientFactory page exception: ${error}` });
      }
    } else {
      DebugAnalyticsTrackerService.page(pageEvent);
    }
  };

  const identify = async (userId: string, options?: AnalyticsIdentifyOptions): Promise<void> => {
    const identifyEvent = AnalyticsEventFactory.createIdentifyEvent(userId, {
      ...options,
      locale: options?.locale || clientOptions?.locale,
      anonymousId: options?.anonymousId || clientOptions?.anonymousId,
      properties: options?.properties ?? {},
    });

    if (!DebugAnalyticsTrackerService.isEnabled()) {
      try {
        await retryAsync(UsageTrackerService.identify, [identifyEvent, toRestOptions(apiOptions)], 2);
      } catch (error) {
        getTelemetryService().trackException({ error: `AnalyticsClientFactory identify exception: ${error}` });
      }
    } else {
      DebugAnalyticsTrackerService.identify(identifyEvent);
    }
  };

  return {
    track,
    page,
    identify,
  };
};

export const AnalyticsClientFactory = {
  create,
};
